//@ts-check
/**
 * Constants for RCS Level-of-Detail (LOD) management and related configuration.
 */

/**
 * LOD mapping mode:
 * - 'threshold': LOD levels based on point count thresholds (uses RCS_LOD_THRESHOLDS)
 * - 'direct': Direct mapping from source octree levels to target LOD levels
 *
 * In 'direct' mode with DIRECT_MODE_LEVELS_IN_LOD0:
 *   LOD 0 = source levels 0 to (DIRECT_MODE_LEVELS_IN_LOD0 - 1) (combined)
 *   LOD 1 = source level DIRECT_MODE_LEVELS_IN_LOD0
 *   LOD 2 = source level DIRECT_MODE_LEVELS_IN_LOD0 + 1
 *   etc.
 *
 * In 'threshold' mode:
 *   LOD levels determined by cumulative point counts
 *
 * @type {'threshold'|'direct'}
 */
export const LOD_MAPPING_MODE = 'direct';

/**
 * Number of source octree levels to combine into LOD 0 in 'direct' mode
 *
 * The coarsest octree levels often have very few points, so it's more efficient
 * to combine several of them into a single LOD level.
 *
 * Examples:
 * - If set to 1: LOD 0 = level 0, LOD 1 = level 1, LOD 2 = level 2, etc.
 * - If set to 3: LOD 0 = levels 0-2, LOD 1 = level 3, LOD 2 = level 4, etc.
 * - If set to 5: LOD 0 = levels 0-4, LOD 1 = level 5, LOD 2 = level 6, etc.
 *
 * Only used when LOD_MAPPING_MODE = 'direct'
 *
 * @type {number}
 */
export const DIRECT_MODE_LEVELS_IN_LOD0 = 7;

/**
 * Point count thresholds for each LOD level (used in 'threshold' mode)
 * Each entry defines the minimum point count for that LOD level
 *
 * Example: [1000, 10_000, 30_000, Infinity]
 * - LOD 0: 0 to 1,000 points (lowest detail)
 * - LOD 1: 1,000 to 10,000 points (medium detail)
 * - LOD 2: 10,000 to 30,000 points (high detail)
 * - LOD 3: 30,000+ points (highest detail)
 */
// export const RCS_LOD_THRESHOLDS = [100, 3000, 10_000, 30_000, Infinity];
export const RCS_LOD_THRESHOLDS = [300, 1000, 3000, 6000, 10_000, 15_000, 30_000, 60_000, 90_000, Infinity];
//export const RCS_LOD_THRESHOLDS = [300, 3000, 10_000, 30_000, Infinity];
//export const RCS_LOD_THRESHOLDS = [0, Infinity];

/**
 * Maximum number of octree levels stored per umbrella node in the RCS spec.
 * @type {number}
 */
const RCS_MAX_OCTREE_LEVELS = 32;

/**
 * Stride for packing (umbrellaIndex, lodLevel) into a single numeric `fragmentIdMap` key
 * (`umbrellaIndex * MAX_LOD_LEVELS + lodLevel`). Must be strictly greater than the maximum
 * LOD level index that {@link ./RcsLodMapping.js#getRequiredLodLevels getRequiredLodLevels} can return.
 *
 * In direct mode the max LOD index is `RCS_MAX_OCTREE_LEVELS - DIRECT_MODE_LEVELS_IN_LOD0`;
 * in threshold mode it is `RCS_LOD_THRESHOLDS.length - 1`. We add 1 to convert
 * the max LOD index into the number of distinct LOD levels (indices 0..max inclusive).
 * @type {number}
 */
export const MAX_LOD_LEVELS =
(LOD_MAPPING_MODE === 'direct' ?
RCS_MAX_OCTREE_LEVELS - DIRECT_MODE_LEVELS_IN_LOD0 :
RCS_LOD_THRESHOLDS.length - 1) +
1;

/**
 * This factor determines how we scale the feature size derived from the point cloud
 * resolution relative to object size. This will impact both small feature culling and
 * LOD selection when compared to meshes.
 *
 * For meshes we use the size of the largest object in the node as reference to decide on
 * priority. For points, we don't have a largest object. This factor determines how to map
 * the feature size of 0.5 ^ level * nodeSize to a feature size for the sake of comparision.
 *
 * Making this too small will cause nodes to be small feature culled.
 *
 * TODO: Improve the computation of this factor. It currently depends on node size and level.
 *       for inner nodes the node size still decreases, but for leaves it stays the same. So
 *       the computation is inconsistent, we should turn this into a reliable geometric
 *       measure.
 */
export const FEATURE_SIZE_SCALING_FACTOR = 20;

/**
 * Maximum number of parallel in-flight requests to the RCS server
 */
export const RCS_NUM_IN_FLIGHT_REQUESTS = 3000;

/**
 * Reduced async promotion limit for localhost direct-file loads (dev server only).
 * Loading RCS from localhost is for debugging; the HTTP/1.1 dev server cannot
 * sustain thousands of concurrent range requests.
 */
export const RCS_NUM_IN_FLIGHT_REQUESTS_HTTP = 300;

/**
 * Proxy Configuration
 *
 * Ranger HTTP Range Request Proxy (Rust/Axum)
 *  - GET with Range header containing comma-separated ranges
 *  - Returns standard RFC 7233 multipart/byteranges response
 *  - Example: 'http://localhost:8888'
 *
 * @type {string|null} Proxy URL or null to disable proxy
 */
//export const HTTP2_PROXY_URL = null;
export let HTTP2_PROXY_URL = 'https://localhost:8888';

/**
 * Proxy type: 'ranger', 'ranger_bin', or 'ranger_ws'
 * - 'ranger': RFC 7233 compliant multipart/byteranges proxy
 * - 'ranger_bin': Binary protocol over HTTP POST
 * - 'ranger_ws': WebSocket-based binary protocol for persistent connections on localhost
 * - 'ranger_ws_remote': WebSocket-based binary protocol for persistent connections to remote server
 * - 'ranger_ws_remote_old': WebSocket-based binary protocol for persistent connections to remote server using the old ranger poc backend
 * @type {'ranger'|'ranger_bin'|'ranger_ws'|'ranger_ws_remote'|'ranger_ws_remote_old'}
 */
export const PROXY_TYPE = 'ranger_ws_remote';

// @ts-ignore
export const USE_OLD_RANGER = PROXY_TYPE === 'ranger_ws_remote_old';
// TODO: Use production endpoints before deploying to production
export const HTTP2_PROXY_URL_REMOTE = USE_OLD_RANGER ? 'https://vds.dev.usa.autodesk.com/ranger' : 'https://cdn-stg.derivative.autodesk.com/cdn';
//export const HTTP2_PROXY_URL = 'http://localhost:8888'; // For ranger type

/**
 * Maximum number of range requests to batch in a single proxy request
 * @type {number}
 */
export const PROXY_BATCH_SIZE = 30;

/**
 * Maximum time to wait (in milliseconds) before sending a partial batch
 * @type {number}
 */
export const PROXY_BATCH_TIMEOUT_MS = 30;

/**
 * Maximum total bytes to accumulate before immediately flushing the batch
 * This prevents large requests from waiting for the timeout or batch size limit
 * @type {number}
 */
export const PROXY_BATCH_BYTES_THRESHOLD = 1000 * 1024; // ~1 MB

/**
 * Instance tree detail level options:
 * - 'simple': Single flat tree with all fragments sharing one dbId (dbId = 1)
 * - 'debugOctree': DEBUGGING ONLY — hierarchical tree with Root → LOD Levels → Individual Nodes (dbId = fragId)
 *
 * This is a live binding consumed by the loader at model-load time, so it can be
 * overridden via {@link setInstanceTreeMode} before loading a model (e.g. from tests
 * that need a specific layout regardless of the shipped default).
 * @type {'simple'|'debugOctree'}
 */
export let INSTANCE_TREE_MODE = 'debugOctree';

/**
 * Override {@link INSTANCE_TREE_MODE}. Must be called before the model is loaded,
 * since the instance tree layout and fragment→dbId mapping are decided at load time.
 * @param {'simple'|'debugOctree'} mode
 */
export function setInstanceTreeMode(mode) {
  if (mode !== 'simple' && mode !== 'debugOctree') {
    throw new Error(`Invalid INSTANCE_TREE_MODE: ${mode}. Must be 'simple' or 'debugOctree'.`);
  }
  INSTANCE_TREE_MODE = mode;
}